import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertTriangle, FileText, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import Footer from '../components/Footer';
import VoiceTextInput from '../components/VoiceTextInput';
import { authFetch } from '../lib/authFetch';
import useAllowPrint from '../components/useAllowPrint';

// ====================================================================
// SUBSTANCE LIST for dropdown
// ====================================================================
const SUBSTANCE_OPTIONS = [
  'Alcohol',
  'Opiates / Heroin',
  'Methamphetamine / Amphetamines',
  'Cocaine / Crack',
  'Cannabis / Marijuana',
  'Benzodiazepines',
  'Prescription Opioids',
  'Methadone / Buprenorphine',
  'MDMA / Ecstasy',
  'Hallucinogens (LSD, Psilocybin)',
  'Ketamine',
  'GHB / GBL',
  'Inhalants / Volatile Substances',
  'Steroids',
  'Synthetic Cannabinoids (Spice/K2)',
  'Nicotine / Tobacco',
  'Gambling (Behavioural Addiction)',
  'Other',
];

// ====================================================================
// MOTIVATION OPTIONS for radio
// ====================================================================
const MOTIVATION_OPTIONS = [
  'Self-Motivated',
  'Legal Pressure',
  'Family Pressure',
  'Employer Mandated',
  'Other',
];

// ====================================================================
// FIELD TYPES:
//   'text'     — VoiceTextInput (spoken or typed), with optional wordLimit
//   'dropdown' — Select dropdown with optional "Other" text input
//   'radio'    — Multiple choice radio buttons
// ====================================================================

// ====================================================================
// SELF-WORK TASK DEFINITIONS
// ====================================================================
const SELF_WORK_TASKS = {
  'sw-0001': {
    id: 'sw-0001',
    code: '0001',
    title: 'The H.O.W. Commitment',
    intro: 'Your input here forms the baseline for your Case Management Plan. Complete each section with H.O.W. (Honesty, Open-Mindedness and Willingness).',
    sections: [
      // --- TASK 1: H.O.W. ---
      {
        id: 'honesty',
        taskNumber: 1,
        taskLabel: 'The H.O.W. Commitment',
        title: 'Honesty',
        letter: 'H',
        color: 'from-blue-600 to-blue-800',
        accent: 'border-blue-400',
        description: 'Be honest with yourself. If you lie to others or yourself, it will give this disease a stronghold on you again. You are not encouraged to confess wrongdoings now; discuss this later with a sponsor or professional.',
        fields: [
          { fieldId: 'honesty_reflection', type: 'text', label: 'Describe', prompt: 'In your own words, what does Honesty mean to your Recovery? What areas of your life have you been dishonest about?', required: true, rows: 5 },
        ],
      },
      {
        id: 'open_mindedness',
        title: 'Open-Mindedness',
        letter: 'O',
        color: 'from-emerald-600 to-emerald-800',
        accent: 'border-emerald-400',
        description: 'If you keep your mind closed to new ideas, nothing will change. Your life is at stake.',
        fields: [
          { fieldId: 'openmindedness_reflection', type: 'text', label: 'Explain', prompt: 'What new ideas or approaches are you willing to consider? What beliefs have held you back?', required: true, rows: 5 },
        ],
      },
      {
        id: 'willingness',
        title: 'Willingness',
        letter: 'W',
        color: 'from-amber-600 to-amber-800',
        accent: 'border-amber-400',
        description: 'Be willing to learn and make changes necessary to save your life. If an employer required changes to keep your job, you would do it; treat your life with the same urgency.',
        fields: [
          { fieldId: 'willingness_reflection', type: 'text', label: 'Describe', prompt: 'What changes are you willing to make right now? What are you prepared to do differently, starting today?', required: true, rows: 5 },
        ],
      },
      // --- TASK 2: Substance & Catalyst Audit ---
      {
        id: 'substance_audit',
        taskNumber: 2,
        taskLabel: 'The Substance and Catalyst Audit',
        title: 'Primary Substance(s) of Concern',
        letter: '2',
        color: 'from-red-600 to-red-800',
        accent: 'border-red-400',
        description: 'Select your primary substance(s) of concern. You may select more than one.',
        fields: [
          { fieldId: 'primary_substances', type: 'dropdown', label: 'Primary Substance(s)', options: SUBSTANCE_OPTIONS, multiple: true, required: true, otherFieldId: 'primary_substance_other' },
        ],
      },
      {
        id: 'frequency_volume',
        title: 'Frequency and Volume',
        color: 'from-red-600 to-red-800',
        accent: 'border-red-400',
        description: 'How much were you using daily or weekly in the month leading up to today?',
        fields: [
          { fieldId: 'frequency_volume', type: 'text', label: 'Describe', prompt: 'Describe your usage pattern: how often and how much, in the month before today...', required: true, rows: 5, wordLimit: 200 },
        ],
      },
      {
        id: 'catalyst_event',
        title: 'The Catalyst or Crisis Event',
        color: 'from-red-600 to-red-800',
        accent: 'border-red-400',
        description: 'Describe the specific incident or "existential wall" that made you realize it is time to stop. What finally broke through your denial?',
        fields: [
          { fieldId: 'catalyst_event', type: 'text', label: 'Describe', prompt: 'Describe the moment or event that brought you here...', required: true, rows: 8, wordLimit: 500 },
        ],
      },
      // --- TASK 3: Three-Fold Disease Impact ---
      {
        id: 'physical_loss',
        taskNumber: 3,
        taskLabel: 'Identifying the Three-Fold Disease Impact',
        title: 'Physical',
        letter: '3',
        color: 'from-purple-600 to-purple-800',
        accent: 'border-purple-400',
        description: 'How did your body feel when your crisis happened?',
        fields: [
          { fieldId: 'physical_loss', type: 'text', label: 'Describe', prompt: 'Describe...', required: true, rows: 5, wordLimit: 200 },
        ],
      },
      {
        id: 'mental_denial',
        title: 'Mental',
        color: 'from-purple-600 to-purple-800',
        accent: 'border-purple-400',
        description: 'Did you have any denial come up (reasons or blame for the crisis other than yourself)?',
        fields: [
          { fieldId: 'mental_denial', type: 'text', label: 'Explain', prompt: 'Explain...', required: true, rows: 5, wordLimit: 200 },
        ],
      },
      {
        id: 'emotional_volatility',
        title: 'Emotional',
        color: 'from-purple-600 to-purple-800',
        accent: 'border-purple-400',
        description: 'How did you feel when the crisis happened?',
        fields: [
          { fieldId: 'emotional_volatility', type: 'text', label: 'Explain', prompt: 'Explain...', required: true, rows: 5, wordLimit: 200 },
        ],
      },
      // --- TASK 4: Motivation Review ---
      {
        id: 'source_of_entry',
        taskNumber: 4,
        taskLabel: 'The Motivation Review (Pressure vs. Sovereignty)',
        title: 'The Source of Entry',
        letter: '4',
        color: 'from-cyan-600 to-cyan-800',
        accent: 'border-cyan-400',
        description: 'Was there any other pressure on you to start Axiom Recovery Programme?',
        fields: [
          { fieldId: 'source_of_entry', type: 'dropdown', label: 'Select All That Apply', options: MOTIVATION_OPTIONS, multiple: true, required: true, otherFieldId: 'source_of_entry_other' },
          { fieldId: 'source_of_entry_describe', type: 'text', label: 'Describe', prompt: 'Describe the pressure or motivation behind your decision to start the programme...', required: true, rows: 5, wordLimit: 300 },
        ],
      },
    ],
    consent: {
      text: 'I acknowledge that if I do not thoroughly follow this path, the outcome is Death, Insanity or Incarceration. I commit to the effort required, One Day at a Time.',
      fieldId: 'how_consent',
    },
    submitLabel: 'SUBMIT SELF-WORK',
  },
};

// ====================================================================
// WORD COUNT HELPER
// ====================================================================
const countWords = (text) => {
  if (!text || !text.trim()) return 0;
  return text.trim().split(/\s+/).length;
};

// ====================================================================
// COMPONENT
// ====================================================================
function SelfWorkTaskPage() {
  useAllowPrint();
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({});
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const task = SELF_WORK_TASKS[taskId];

  const fetchExisting = useCallback(async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/self-work/${taskId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.completed) {
          setIsCompleted(true);
          setFormData(data.form_data || {});
          setConsentChecked(true);
        } else if (data.form_data) {
          setFormData(data.form_data);
        }
      }
    } catch (error) {
      console.error('Error fetching self-work task:', error);
    } finally {
      setIsLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    if (task) fetchExisting();
  }, [task, fetchExisting]);

  const handleChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const toggleDropdownItem = (fieldId, item) => {
    setFormData(prev => {
      const current = prev[fieldId] || [];
      if (current.includes(item)) {
        return { ...prev, [fieldId]: current.filter(i => i !== item) };
      }
      return { ...prev, [fieldId]: [...current, item] };
    });
  };

  const handleSubmit = async () => {
    if (!task) return;

    // Validate all required fields across all sections
    for (const section of task.sections) {
      for (const field of section.fields) {
        if (!field.required) continue;
        const val = formData[field.fieldId];
        if (field.type === 'text' && (!val || !val.trim())) {
          toast.error(`Please complete "${section.title}"`);
          return;
        }
        if (field.type === 'dropdown' && (!val || (Array.isArray(val) && val.length === 0))) {
          toast.error(`Please select at least one option in "${section.title}"`);
          return;
        }
        if (field.type === 'radio' && !val) {
          toast.error(`Please make a selection in "${section.title}"`);
          return;
        }
      }
    }
    if (task.consent && !consentChecked) {
      toast.error('Please read and accept the commitment to proceed');
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = { ...formData };
      if (task.consent) {
        submitData[task.consent.fieldId] = true;
        submitData[`${task.consent.fieldId}_at`] = new Date().toISOString();
      }

      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/self-work/${taskId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form_data: submitData }),
      });

      if (response.ok) {
        setIsCompleted(true);
        toast.success('Self-work submitted successfully');
      } else {
        toast.error('Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- RENDER HELPERS ----

  const renderField = (field, section) => {
    switch (field.type) {
      case 'text':
        return renderTextField(field);
      case 'dropdown':
        return renderDropdownField(field);
      case 'radio':
        return renderRadioField(field);
      default:
        return null;
    }
  };

  const renderTextField = (field) => {
    const wc = countWords(formData[field.fieldId] || '');
    const overLimit = field.wordLimit && wc > field.wordLimit;

    return (
      <div className="mb-3" key={field.fieldId}>
        <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">
          {field.label} {field.required && <span className="text-red-500">*</span>}
        </p>
        <VoiceTextInput
          value={formData[field.fieldId] || ''}
          onChange={(v) => handleChange(field.fieldId, v)}
          placeholder={field.prompt}
          rows={field.rows || 5}
          testId={`sw-field-${field.fieldId}`}
          className={isCompleted ? 'opacity-75' : ''}
        />
        {field.wordLimit && (
          <p className={`text-xs mt-1 text-right ${overLimit ? 'text-red-500 font-bold' : 'text-zinc-400'}`}>
            {wc} / {field.wordLimit} words
          </p>
        )}
      </div>
    );
  };

  const renderDropdownField = (field) => {
    const selected = formData[field.fieldId] || [];
    const showOther = field.otherFieldId && selected.includes('Other');

    return (
      <div className="mb-3" key={field.fieldId}>
        <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">
          {field.label} {field.required && <span className="text-red-500">*</span>}
        </p>
        <div className="grid grid-cols-1 gap-2" data-testid={`sw-field-${field.fieldId}`}>
          {field.options.map((opt) => {
            const isSelected = selected.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                disabled={isCompleted}
                onClick={() => toggleDropdownItem(field.fieldId, opt)}
                className={`text-left px-4 py-3 rounded-lg border-2 transition-all text-sm ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 text-blue-900 font-medium'
                    : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300'
                } ${isCompleted ? 'opacity-75' : ''}`}
                data-testid={`sw-opt-${opt.toLowerCase().replace(/[^a-z0-9]/g, '_')}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'border-blue-500 bg-blue-500' : 'border-zinc-300'
                  }`}>
                    {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                  </div>
                  {opt}
                </div>
              </button>
            );
          })}
        </div>
        {showOther && (
          <div className="mt-3">
            <VoiceTextInput
              value={formData[field.otherFieldId] || ''}
              onChange={(v) => handleChange(field.otherFieldId, v)}
              placeholder="Please specify..."
              rows={2}
              testId={`sw-field-${field.otherFieldId}`}
              className={isCompleted ? 'opacity-75' : ''}
            />
          </div>
        )}
        {selected.length > 0 && (
          <p className="text-xs text-zinc-500 mt-2">{selected.length} selected</p>
        )}
      </div>
    );
  };

  const renderRadioField = (field) => {
    const selected = formData[field.fieldId];

    return (
      <div className="mb-3" key={field.fieldId}>
        <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">
          {field.label} {field.required && <span className="text-red-500">*</span>}
        </p>
        <div className="space-y-2" data-testid={`sw-field-${field.fieldId}`}>
          {field.options.map((opt) => {
            const isSelected = selected === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                disabled={isCompleted}
                onClick={() => handleChange(field.fieldId, opt.value)}
                className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all text-sm ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 text-blue-900 font-medium'
                    : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300'
                } ${isCompleted ? 'opacity-75' : ''}`}
                data-testid={`sw-radio-${opt.value}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'border-blue-500' : 'border-zinc-300'
                  }`}>
                    {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />}
                  </div>
                  {opt.label}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ---- NOT FOUND / LOADING ----

  if (!task) {
    return (
      <div className="min-h-screen bg-zinc-100">
        <PageHeader title="Self-Work" />
        <main className="pt-20 pb-6 px-4">
          <div className="max-w-md mx-auto text-center py-12">
            <FileText className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
            <p className="text-zinc-600">Self-work task not found.</p>
            <Button onClick={() => navigate('/self-work')} className="mt-4">Back to Self-Work</Button>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-100">
        <PageHeader title={`Self-Work: ${task.code}`} />
        <main className="pt-20 pb-6 px-4">
          <div className="max-w-md mx-auto text-center py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        </main>
      </div>
    );
  }

  // ---- MAIN RENDER ----

  // Track which task numbers we've seen so we can show task dividers
  let lastTaskNumber = null;

  return (
    <div className="min-h-screen bg-zinc-100 pb-20">
      <PageHeader title={`Self-Work: ${task.code}`} />

      <main className="pt-20 px-4 pb-6">
        <div className="max-w-lg mx-auto">

          {/* Completed Banner */}
          {isCompleted && (
            <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="sw-completed-banner">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-bold text-green-800">Submitted</p>
                <p className="text-sm text-green-700">This self-work has been completed. You can review your responses below.</p>
              </div>
            </div>
          )}

          {/* Task Header */}
          <div className="bg-white rounded-xl border border-zinc-200 p-6 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-mono font-bold px-2 py-1 rounded bg-zinc-900 text-white">
                SW-{task.code}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-3" data-testid="sw-task-title">
              {task.title}
            </h1>
            <p className="text-sm text-zinc-600 leading-relaxed">
              {task.intro}
            </p>
          </div>

          {/* Sections */}
          {task.sections.map((section) => {
            // Show a task divider when we encounter a new taskNumber
            let showDivider = false;
            if (section.taskNumber && section.taskNumber !== lastTaskNumber) {
              showDivider = true;
              lastTaskNumber = section.taskNumber;
            }

            return (
              <React.Fragment key={section.id}>
                {/* Task Divider */}
                {showDivider && section.taskNumber > 1 && (
                  <div className="flex items-center gap-3 my-8" data-testid={`sw-task-divider-${section.taskNumber}`}>
                    <div className="flex-1 h-px bg-zinc-300" />
                    <span className="text-xs font-black text-zinc-400 uppercase tracking-widest">
                      Task {section.taskNumber}: {section.taskLabel}
                    </span>
                    <div className="flex-1 h-px bg-zinc-300" />
                  </div>
                )}

                <div
                  className="mb-6"
                  data-testid={`sw-section-${section.id}`}
                >
                  {/* Section Header */}
                  <div className={`bg-gradient-to-r ${section.color} rounded-t-xl p-4`}>
                    <div className="flex items-center gap-3">
                      {section.letter && (
                        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                          <span className="text-xl font-black text-white">{section.letter}</span>
                        </div>
                      )}
                      <div>
                        {section.taskNumber && !section.letter && (
                          <p className="text-xs text-white/60 font-bold uppercase tracking-wider">Task {section.taskNumber}</p>
                        )}
                        <h2 className="text-lg font-bold text-white">{section.title}</h2>
                      </div>
                    </div>
                  </div>

                  {/* Section Content */}
                  <div className={`bg-white border-2 ${section.accent} border-t-0 rounded-b-xl p-5`}>
                    {section.description && (
                      <p className="text-sm text-zinc-700 leading-relaxed mb-4">
                        {section.description}
                      </p>
                    )}
                    {section.fields.map((field) => renderField(field, section))}
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {/* Consent Section */}
          {task.consent && (
            <div className="bg-zinc-900 rounded-xl p-6 mb-6" data-testid="sw-consent-section">
              <div className="flex items-start gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs font-bold text-amber-400 uppercase tracking-wider">Consent</p>
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed mb-5 italic">
                "{task.consent.text}"
              </p>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent-check"
                  checked={consentChecked}
                  onCheckedChange={(checked) => !isCompleted && setConsentChecked(checked)}
                  disabled={isCompleted}
                  className="mt-0.5 border-zinc-500 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                  data-testid="sw-consent-checkbox"
                />
                <label htmlFor="consent-check" className="text-sm text-zinc-400 cursor-pointer select-none">
                  I have read, understood and commit to the above statement.
                </label>
              </div>
            </div>
          )}

          {/* Submit Button */}
          {!isCompleted && (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full bg-zinc-950 text-white hover:bg-zinc-800 h-14 text-base font-bold tracking-wider rounded-xl"
              data-testid="sw-submit-btn"
            >
              {isSubmitting ? 'Submitting...' : task.submitLabel}
            </Button>
          )}

          {/* Return to Self-Work */}
          {isCompleted && (
            <Button
              onClick={() => navigate('/self-work')}
              variant="outline"
              className="w-full h-12 rounded-xl"
              data-testid="sw-return-btn"
            >
              Return to Self-Work
            </Button>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default SelfWorkTaskPage;
